import type { TFunction } from 'i18next';
import React from 'react';
import { StyleSheet, Text } from 'react-native';

import type {
	DuelRewardHistoryEntryDto,
	DuelRewardMethod,
	DuelRewardStatus,
} from '../../../services/api';
import { duelText } from './duelsText';
import { formatScore } from './duelsTheme';
import { duelTextStyles } from './DuelsUi';

const STATUS_KEYS: Record<DuelRewardStatus, string> = {
	Unlocked: 'T_SITE_DUELS_REWARD_STATUS_UNLOCKED',
	Claimed: 'T_SITE_DUELS_REWARD_STATUS_CLAIMED',
	Granted: 'T_SITE_DUELS_REWARD_STATUS_GRANTED',
	GrantFailed: 'T_SITE_DUELS_REWARD_STATUS_FAILED',
	Requested: 'T_SITE_DUELS_REWARD_STATUS_REQUESTED',
	UnderReview: 'T_SITE_DUELS_REWARD_STATUS_UNDER_REVIEW',
	Approved: 'T_SITE_DUELS_REWARD_STATUS_APPROVED',
	Rejected: 'T_SITE_DUELS_REWARD_STATUS_REJECTED',
	Fulfilled: 'T_SITE_DUELS_REWARD_STATUS_FULFILLED',
	Failed: 'T_SITE_DUELS_REWARD_STATUS_FAILED',
	Cancelled: 'T_SITE_DUELS_REWARD_STATUS_CANCELLED',
	Expired: 'T_SITE_DUELS_REWARD_STATUS_EXPIRED',
};

const STATUS_INFO_KEYS: Partial<Record<DuelRewardStatus, string>> = {
	Requested: 'T_SITE_DUELS_STATUS_INFO_REQUESTED',
	UnderReview: 'T_SITE_DUELS_STATUS_INFO_UNDER_REVIEW',
	Approved: 'T_SITE_DUELS_STATUS_INFO_APPROVED',
	Rejected: 'T_SITE_DUELS_STATUS_INFO_REJECTED',
	Fulfilled: 'T_SITE_DUELS_STATUS_INFO_FULFILLED',
	Failed: 'T_SITE_DUELS_STATUS_INFO_FAILED',
	Cancelled: 'T_SITE_DUELS_STATUS_INFO_CANCELLED',
	Expired: 'T_SITE_DUELS_STATUS_INFO_EXPIRED',
};

const TIMELINE_STATUS: Record<string, DuelRewardStatus> = {
	requested: 'Requested',
	review_started: 'UnderReview',
	approved: 'Approved',
	rejected: 'Rejected',
	fulfilled: 'Fulfilled',
	fulfilment_failed: 'Failed',
	cancelled: 'Cancelled',
	expired: 'Expired',
};

const PILL_COLORS: Record<DuelRewardStatus, { text: string; border: string; fill: string }> = {
	Unlocked: { text: '#2FE07A', border: '#2FE07A', fill: 'rgba(47,224,122,0.12)' },
	Claimed: { text: '#3A1200', border: '#FFE9A8', fill: '#F4AE2B' },
	Granted: { text: '#2FE07A', border: '#2FE07A', fill: 'rgba(47,224,122,0.12)' },
	GrantFailed: { text: '#FF6FD8', border: '#FF6FD8', fill: 'rgba(255,111,216,0.12)' },
	Requested: { text: '#3A1200', border: '#FFE9A8', fill: '#F4AE2B' },
	UnderReview: { text: '#3A1200', border: '#FFE9A8', fill: '#F4AE2B' },
	Approved: { text: '#2FE07A', border: '#2FE07A', fill: 'rgba(47,224,122,0.12)' },
	Rejected: { text: '#FF6FD8', border: '#FF6FD8', fill: 'rgba(255,111,216,0.12)' },
	Fulfilled: { text: '#2FE07A', border: '#2FE07A', fill: 'rgba(47,224,122,0.12)' },
	Failed: { text: '#FF6FD8', border: '#FF6FD8', fill: 'rgba(255,111,216,0.12)' },
	Cancelled: { text: '#C9B7A6', border: '#C9B7A6', fill: 'rgba(201,183,166,0.12)' },
	Expired: { text: '#C9B7A6', border: '#C9B7A6', fill: 'rgba(201,183,166,0.12)' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatShortDate = (iso: string | null | undefined, withYear = true) => {
	if (!iso) return '—';
	const date = new Date(iso);
	return withYear
		? `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
		: `${date.getDate()} ${MONTHS[date.getMonth()]}`;
};

export const rewardStatusText = (t: TFunction, status: DuelRewardStatus) =>
	duelText(t, STATUS_KEYS[status]);

export const rewardStatusInfo = (t: TFunction, status: DuelRewardStatus) =>
	STATUS_INFO_KEYS[status] ? duelText(t, STATUS_INFO_KEYS[status] as string) : null;

export const rewardRequestId = (entitlementId: string) =>
	`#RWG-${entitlementId.slice(-6).toUpperCase()}`;

export const rewardPillText = (t: TFunction, status: DuelRewardStatus) =>
	rewardStatusText(t, status === 'Requested' ? 'UnderReview' : status);

export const DUEL_REWARD_METHODS: DuelRewardMethod[] = ['PayPal', 'GiftCard', 'Voucher'];

const METHOD_KEYS: Record<DuelRewardMethod, { name: string; hint: string; email: string }> = {
	PayPal: {
		name: 'T_SITE_DUELS_METHOD_PAYPAL',
		hint: 'T_SITE_DUELS_METHOD_PAYPAL_HINT',
		email: 'T_SITE_DUELS_EMAIL_PAYPAL',
	},
	GiftCard: {
		name: 'T_SITE_DUELS_METHOD_GIFT_CARD',
		hint: 'T_SITE_DUELS_METHOD_GIFT_CARD_HINT',
		email: 'T_SITE_DUELS_EMAIL_GIFT_CARD',
	},
	Voucher: {
		name: 'T_SITE_DUELS_METHOD_VOUCHER',
		hint: 'T_SITE_DUELS_METHOD_VOUCHER_HINT',
		email: 'T_SITE_DUELS_EMAIL_VOUCHER',
	},
};

export const rewardMethodText = (
	t: TFunction,
	method: DuelRewardMethod,
	part: 'name' | 'hint' | 'email' = 'name',
) => duelText(t, METHOD_KEYS[method][part]);

export const lastRequestMethod = (history: DuelRewardHistoryEntryDto[]) =>
	[...history].reverse().find(entry => entry.action === 'requested')?.method ?? null;

type RewardTitleSource = {
	rewardType: string | null;
	title: string | null;
	value: number | null;
	quantity: number | null;
};

export const rewardTitle = (t: TFunction, reward: RewardTitleSource) => {
	if (reward.value === null) return reward.title ?? '';
	const amount = formatScore(reward.value * (reward.quantity ?? 1));
	switch (reward.rewardType) {
		case 'Chips':
			return duelText(t, 'T_SITE_DUELS_REWARD_CHIPS', [amount]);
		case 'Experience':
			return duelText(t, 'T_SITE_DUELS_REWARD_XP', [amount]);
		case 'VipDays':
			return duelText(t, 'T_SITE_DUELS_REWARD_VIP_DAYS', [amount]);
		default:
			return reward.title ?? '';
	}
};

export const lastRequestedAt = (history: DuelRewardHistoryEntryDto[]) =>
	[...history].reverse().find(entry => entry.action === 'requested')?.at ?? null;

export const rewardTimeline = (history: DuelRewardHistoryEntryDto[]) => {
	const startIndex = history.map(entry => entry.action).lastIndexOf('requested');
	return history
		.slice(Math.max(0, startIndex))
		.filter(entry => TIMELINE_STATUS[entry.action])
		.map(entry => ({ status: TIMELINE_STATUS[entry.action], at: entry.at }));
};

export const asRewardStatus = (status: string | null | undefined): DuelRewardStatus | null =>
	status && Object.prototype.hasOwnProperty.call(PILL_COLORS, status)
		? (status as DuelRewardStatus)
		: null;

type PillProps = { status: DuelRewardStatus; scale: number };

export const DuelRewardStatusPill = ({ status, scale, label }: PillProps & { label: string }) => {
	const colors = PILL_COLORS[status];
	return (
		<Text
			numberOfLines={1}
			style={[
				duelTextStyles.title,
				styles.pill,
				{
					color: colors.text,
					borderColor: colors.border,
					backgroundColor: colors.fill,
					fontSize: 11 * scale,
					paddingHorizontal: 10 * scale,
					paddingVertical: 3 * scale,
					borderRadius: 10 * scale,
				},
			]}
		>
			{label}
		</Text>
	);
};

const styles = StyleSheet.create({
	pill: {
		alignSelf: 'flex-start',
		borderWidth: 1,
		overflow: 'hidden',
	},
});
